"use client";

import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";

export type PushStatus =
  | "idle"
  | "subscribed"
  | "denied"
  | "unsupported"
  | "loading";

const PUSH_OPT_OUT_KEY = "push-opt-out";

function isPushOptedOut(): boolean {
  try {
    return localStorage.getItem(PUSH_OPT_OUT_KEY) === "1";
  } catch {
    return false;
  }
}

function urlBase64ToUint8Array(base64String: string): ArrayBuffer {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const rawData = atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray.buffer as ArrayBuffer;
}

/**
 * Hook global de Web Push subscription. Pede permissao na primeira chamada,
 * registra Service Worker, sobe a subscription pro backend via
 * `POST /notifications/subscribe`.
 *
 * Uso:
 *   - Plugar em `app/dashboard/layout.tsx` pra subscription rolar uma vez
 *     ao montar o dashboard (chamada idempotente — re-subscribe atualiza).
 *   - Toggle no perfil: `subscribe()` pra ativar, `unsubscribe()` pra
 *     desativar (revoga subscription do browser + deleta do banco).
 *
 * O parametro `autoSubscribe` controla se pede permissao automaticamente
 * no mount (default true). Em telas de settings, passar false pra so
 * disparar quando user clicar.
 */
export function usePushSubscription({
  autoSubscribe = true,
}: { autoSubscribe?: boolean } = {}) {
  const [status, setStatus] = useState<PushStatus>("loading");

  const subscribe = useCallback(async (): Promise<PushStatus> => {
    if (typeof window === "undefined") return "unsupported";
    if (!("serviceWorker" in navigator) || !("PushManager" in window)) {
      setStatus("unsupported");
      return "unsupported";
    }
    if (Notification.permission === "denied") {
      setStatus("denied");
      return "denied";
    }

    try {
      const reg = await navigator.serviceWorker.register("/sw.js");

      // A subscription do PushManager pertence ao BROWSER, nao ao usuario: dois
      // logins no mesmo perfil compartilham o mesmo endpoint. Antes, um `existing`
      // fazia o hook retornar "subscribed" sem POST nenhum — quem entrasse depois
      // via a UI confirmar "notificacoes ativadas" enquanto o servidor continuava
      // com o endpoint amarrado no usuario ANTERIOR, e nunca recebia push.
      // Por isso o POST acontece SEMPRE: o upsert do backend re-aponta o endpoint
      // pra sessao atual (`update: { userId }`), que e o dono legitimo do device.
      let sub = await reg.pushManager.getSubscription();

      if (!sub) {
        const perm = await Notification.requestPermission();
        if (perm !== "granted") {
          setStatus("denied");
          return "denied";
        }

        const { publicKey } = await api.request<{ publicKey: string }>(
          "/notifications/vapid-public-key",
        );
        if (!publicKey) {
          // VAPID nao configurado no backend — push desabilitado mas nao eh erro
          setStatus("unsupported");
          return "unsupported";
        }

        sub = await reg.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(publicKey),
        });
      }

      const subJson = sub.toJSON();

      await api.request("/notifications/subscribe", {
        method: "POST",
        body: JSON.stringify({
          endpoint: subJson.endpoint,
          p256dh: subJson.keys?.p256dh || "",
          authKey: subJson.keys?.auth || "",
          userAgent: navigator.userAgent,
        }),
        headers: { "Content-Type": "application/json" },
      });

      try { localStorage.removeItem(PUSH_OPT_OUT_KEY); } catch {}
      setStatus("subscribed");
      return "subscribed";
    } catch (err) {
      console.warn("[push] subscription failed", err);
      setStatus("idle");
      return "idle";
    }
  }, []);

  const unsubscribe = useCallback(async (): Promise<void> => {
    if (typeof window === "undefined") return;
    if (!("serviceWorker" in navigator)) return;

    try {
      const reg = await navigator.serviceWorker.getRegistration();
      if (!reg) return;

      const sub = await reg.pushManager.getSubscription();
      if (!sub) {
        setStatus("idle");
        return;
      }

      const endpoint = sub.endpoint;
      await sub.unsubscribe();
      try { localStorage.setItem(PUSH_OPT_OUT_KEY, "1"); } catch {}

      // Best-effort: avisa backend pra apagar a row. Falha de rede nao
      // quebra a UX — subscription do browser ja foi revogada localmente.
      await api
        .request("/notifications/unsubscribe", {
          method: "DELETE",
          body: JSON.stringify({ endpoint }),
          headers: { "Content-Type": "application/json" },
        })
        .catch(() => {});

      setStatus("idle");
    } catch (err) {
      console.warn("[push] unsubscribe failed", err);
    }
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (!("serviceWorker" in navigator) || !("PushManager" in window)) {
      setStatus("unsupported");
      return;
    }

    // Listener pra navegacao quando user clica notif (SW manda postMessage)
    const handleMessage = (event: MessageEvent) => {
      if (event.data?.type === "navigate" && event.data?.url) {
        window.location.href = event.data.url;
      }
    };
    navigator.serviceWorker.addEventListener("message", handleMessage);

    // Auto-subscribe no mount: se ja tem permissao e subscription, marca
    // subscribed; se nao tem, NAO pede automaticamente — browsers modernos
    // consideram pedir Notification.permission no mount como spam (Chrome
    // bloqueia silenciosamente). User deve ativar via toggle no perfil.
    const checkExisting = async () => {
      if (Notification.permission === "denied") {
        setStatus("denied");
        return;
      }
      try {
        const reg = await navigator.serviceWorker.getRegistration();
        const existing = await reg?.pushManager.getSubscription();
        if (
          autoSubscribe &&
          Notification.permission === "granted" &&
          !isPushOptedOut()
        ) {
          // Re-registra SEMPRE que ha permissao — com ou sem subscription local.
          // Com subscription existente o `subscribe()` nao pede permissao nem
          // cria nada: so refaz o POST, que e o passo que amarra o endpoint a
          // ESTA sessao. Sem isso, quem logasse num browser que ja tinha
          // subscription de outro usuario ficava marcado como "subscribed" pra
          // sempre, sem nenhuma linha no servidor — silencio total, sem erro.
          // O upsert por endpoint torna a chamada idempotente.
          await subscribe();
        } else if (existing) {
          setStatus("subscribed");
        } else {
          setStatus("idle");
        }
      } catch {
        setStatus("idle");
      }
    };
    checkExisting();

    return () => {
      navigator.serviceWorker.removeEventListener("message", handleMessage);
    };
  }, [autoSubscribe, subscribe]);

  return { status, subscribe, unsubscribe };
}
